import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useGetLiveAgentLocationsQuery } from "../redux/api/agentLocationApi";
import { formatLastUpdate, toStaff } from "../pages/GPS/utils";

const HUB_URL = "https://data.realtorscrm.com/signalr";
// The API doc names no hub; "agentlocationhub" 500s on negotiate, so the location methods
// (JoinAgencyGroup / agentLocationUpdate) are assumed to live on the existing notification hub.
const HUB_NAME = "notifcationHub";

/**
 * Loads every agent's latest position once, then keeps it current via SignalR pushes
 * (no polling). Returns UI-shaped staff plus the connection state.
 */
export default function useAgentLiveLocations() {
  const { agencyId, token } = useSelector((state) => state.auth);
  const { data, isLoading, error, refetch } = useGetLiveAgentLocationsQuery(agencyId, {
    skip: !agencyId,
    refetchOnMountOrArgChange: true,
  });

  const [byId, setById] = useState({});
  const [connectionState, setConnectionState] = useState("connecting");
  const [now, setNow] = useState(Date.now());
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  // Seed / re-seed from REST snapshot (initial load and after a reconnect).
  useEffect(() => {
    if (!Array.isArray(data)) return;
    setById((prev) => {
      const next = {};
      data.forEach((row) => {
        next[row.agentId] = toStaff(row, prev[row.agentId]);
      });
      return next;
    });
  }, [data]);

  // Live pushes. The backend is classic ASP.NET SignalR (jQuery client, as in useNotification),
  // not ASP.NET Core, so @microsoft/signalr cannot connect to it.
  useEffect(() => {
    if (!agencyId || !token) return undefined;
    if (!window.$ || !window.$.hubConnection) {
      console.error("SignalR client library not loaded");
      setConnectionState("disconnected");
      return undefined;
    }
    const id = parseInt(agencyId, 10);
    let disposed = false;

    const connection = window.$.hubConnection(HUB_URL, { qs: { id: agencyId, type: "2", token } });
    const hub = connection.createHubProxy(HUB_NAME);

    hub.on("agentLocationUpdate", (update) => {
      setById((prev) => {
        if (!prev[update.agentId]) {
          // Agent we haven't loaded (newly added) — pull a fresh snapshot.
          refetchRef.current();
          return prev;
        }
        return { ...prev, [update.agentId]: toStaff(update, prev[update.agentId]) };
      });
    });

    connection.reconnecting(() => setConnectionState("reconnecting"));
    connection.reconnected(() => {
      hub.invoke("JoinAgencyGroup", id).catch((e) => console.error("Failed to rejoin agency group", e));
      refetchRef.current(); // catch up on anything missed while disconnected
      setConnectionState("connected");
    });
    connection.disconnected(() => !disposed && setConnectionState("disconnected"));

    connection
      .start()
      .done(() => {
        if (disposed) return;
        hub.invoke("JoinAgencyGroup", id).catch((e) => console.error("JoinAgencyGroup failed", e));
        setConnectionState("connected");
      })
      .fail((e) => {
        if (disposed) return;
        console.error("Agent live-location SignalR connection failed", e);
        setConnectionState("disconnected");
      });

    return () => {
      disposed = true;
      connection.stop();
    };
  }, [agencyId, token]);

  // Fallback: if the live connection is down, poll the snapshot so the map still updates.
  useEffect(() => {
    if (connectionState !== "disconnected") return undefined;
    const t = setInterval(() => refetchRef.current(), 15000);
    return () => clearInterval(t);
  }, [connectionState]);

  // Re-render periodically so "x min ago" labels stay fresh.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  const staffList = useMemo(
    () =>
      Object.values(byId).map((s) => ({
        ...s,
        lastUpdate: formatLastUpdate(s.lastUpdateAt, now),
      })),
    [byId, now]
  );

  return { staffList, isLoading, error, connectionState, reload: refetch };
}
